@if (enabled) {
  <!-- Trigger — persistent "How this works" button -->
  <button
    type="button"
    (click)="openPanel()"
    aria-label="How this works"
    class="flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-theme-xs hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:text-white/80 dark:hover:bg-gray-700"
  >
    <lucide-icon [name]="HelpIcon" size="16"></lucide-icon>
    How this works
  </button>

  <app-right-sidebar [isSidebarOpen]="isOpen" [customWidth]="30" (closed)="closePanel()">
    <!-- Header -->
    <div class="mb-3 border-b border-gray-200 pr-8 pb-3">
      <h2 class="text-base font-semibold text-gray-800">How this works</h2>
    </div>

    <!-- Tabs -->
    <div class="mb-4 flex gap-1" role="tablist">
      <button
        type="button"
        role="tab"
        [attr.aria-selected]="activeTab === 'page'"
        (click)="selectTab('page')"
        class="rounded-lg px-3 py-1.5 text-sm font-medium"
        [ngClass]="activeTab === 'page' ? 'bg-brand-50 text-brand-600' : 'text-gray-500 hover:text-gray-700'"
      >
        This page
      </button>
      <button
        type="button"
        role="tab"
        [attr.aria-selected]="activeTab === 'full'"
        (click)="selectTab('full')"
        class="rounded-lg px-3 py-1.5 text-sm font-medium"
        [ngClass]="activeTab === 'full' ? 'bg-brand-50 text-brand-600' : 'text-gray-500 hover:text-gray-700'"
      >
        Full process
      </button>
    </div>

    @if (activeTab === 'page') {
      <!-- ===================== THIS PAGE ===================== -->
      @switch (page) {
        @case ('board') {
          <p class="mb-4 text-sm text-gray-500">Terms and stages shown on Deals, in plain English.</p>

          <div class="space-y-3">
            <div class="rounded-2xl border border-gray-200 bg-white p-5">
              <div class="mb-1">
                <span class="inline-flex items-center rounded-full bg-brand-50 px-2.5 py-0.5 text-xs font-medium text-brand-600">Active opportunity</span>
              </div>
              <p class="text-sm text-gray-700">An event has been created for this prospect, but no viewing has been booked yet. Staff can mark it "In conversation" once they're actively talking to the couple, that's a manual flag, not automatic.</p>
            </div>

            <div class="rounded-2xl border border-gray-200 bg-white p-5">
              <div class="mb-1">
                <span class="inline-flex items-center rounded-full bg-brand-50 px-2.5 py-0.5 text-xs font-medium text-brand-600">Viewing booked</span>
              </div>
              <p class="text-sm text-gray-700">A show-round date has been set. This isn't a status you set manually, it's driven by the date being added to the event.</p>
            </div>

            <div class="rounded-2xl border border-gray-200 bg-white p-5">
              <div class="mb-1">
                <span class="inline-flex items-center rounded-full bg-warning-50 px-2.5 py-0.5 text-xs font-medium text-warning-700">Provisional</span>
              </div>
              <p class="text-sm text-gray-700">The sale is agreed and the paperwork is happening. The date is held for this couple, not yet confirmed. Only one Provisional hold is allowed per date.</p>
            </div>

            <div class="rounded-2xl border border-gray-200 bg-white p-5">
              <div class="mb-1">
                <span class="inline-flex items-center rounded-full bg-success-50 px-2.5 py-0.5 text-xs font-medium text-success-700">Confirmed</span>
              </div>
              <p class="text-sm text-gray-700">The booking is locked in. From here, day to day handling moves to operations, Deals doesn't track it further.</p>
            </div>
          </div>

          <div class="mt-5 border-t border-gray-200 pt-4">
            <p class="mb-2 text-xs font-medium tracking-wide text-gray-500 uppercase">Lost vs cancelled</p>
            <p class="text-sm text-gray-700">These are different outcomes, not two words for the same thing. Lost is a prospect who never confirmed. Cancelled is a booking that was confirmed and later fell through.</p>
          </div>

          <div class="mt-4 border-t border-gray-200 pt-4">
            <p class="mb-2 text-xs font-medium tracking-wide text-gray-500 uppercase">Why some cards don't drag</p>
            <p class="text-sm text-gray-700">A card can only be dragged once it's a single, concrete event. If a contact has more than one open event at once, their card can't be dragged directly, open the contact record instead to move a specific event.</p>
          </div>
        }

        @case ('contact') {
          <p class="mb-4 text-sm text-gray-500">What's on this record, in plain English.</p>

          <div class="space-y-3">
            <div class="rounded-2xl border border-gray-200 bg-white p-5">
              <p class="mb-1 text-xs font-medium tracking-wide text-gray-500 uppercase">Journey</p>
              <p class="text-sm text-gray-700">Shows every quote and event this contact has ever had, in one timeline, so you don't need to check separate lists. It's the single source for this contact, nothing else on the page duplicates it.</p>
            </div>

            <div class="rounded-2xl border border-gray-200 bg-white p-5">
              <div class="mb-1">
                <span class="inline-flex items-center rounded-full bg-brand-50 px-2.5 py-0.5 text-xs font-medium text-brand-600">Detailed quotes (draft event)</span>
              </div>
              <p class="text-sm text-gray-700">This is the same underlying record as a quote, it's just been converted into an event so more detail can be added. It's not confirmed yet. Calling it a "draft event" here is deliberate, the client sees this labelled differently, as "your detailed quotes".</p>
            </div>

            <div class="rounded-2xl border border-gray-200 bg-white p-5">
              <p class="mb-1 text-xs font-medium tracking-wide text-gray-500 uppercase">Multiple events on one contact</p>
              <p class="text-sm text-gray-700">Normal and expected. A couple might have several draft events running at once, for example a cloned event with two things changed. Only one will ever become Confirmed, the rest stay visible as history, they're not clutter.</p>
            </div>

            <div class="rounded-2xl border border-gray-200 bg-white p-5">
              <p class="mb-1 text-xs font-medium tracking-wide text-gray-500 uppercase">Staff-only information</p>
              <p class="text-sm text-gray-700">Internal notes, lead source, and the assigned sales/event manager only show here, never on the client's own view of this booking.</p>
            </div>

            <div class="rounded-2xl border border-gray-200 bg-white p-5">
              <p class="mb-1 text-xs font-medium tracking-wide text-gray-500 uppercase">Lost vs cancelled</p>
              <p class="text-sm text-gray-700">Different outcomes. Lost means this contact never confirmed a booking. Cancelled means a booking was confirmed and later fell through.</p>
            </div>
          </div>
        }

        @case ('quote') {
          <p class="mb-4 text-sm text-gray-500">What's on this quote, in plain English.</p>

          <div class="space-y-3">
            <div class="rounded-2xl border border-gray-200 bg-white p-5">
              <p class="mb-1 text-xs font-medium tracking-wide text-gray-500 uppercase">Candidate dates</p>
              <p class="text-sm text-gray-700">Every date this couple asked about on one quote. Each date has its own price and its own "Convert to event" action, converting one date doesn't use up or remove the others.</p>
            </div>

            <div class="rounded-2xl border border-gray-200 bg-white p-5">
              <p class="mb-1 text-xs font-medium tracking-wide text-gray-500 uppercase">Convert to event</p>
              <p class="text-sm text-gray-700">Turns one chosen date into a full event record, so you can add more detail, guest counts, products, payment schedule. The quote itself is kept, so the other candidate dates are still there if needed later.</p>
            </div>

            <div class="rounded-2xl border border-gray-200 bg-white p-5">
              <p class="mb-1 text-xs font-medium tracking-wide text-gray-500 uppercase">Why this quote shows a stage</p>
              <p class="text-sm text-gray-700">A quote follows the same stages as an event up to a point, but it can only ever reach <span class="font-medium">Viewing booked</span>. Anything beyond that, Provisional, Confirmed, only happens once a date has been converted into an event.</p>
            </div>

            <div class="rounded-2xl border border-gray-200 bg-white p-5">
              <div class="mb-1">
                <span class="inline-flex items-center rounded-full bg-brand-50 px-2.5 py-0.5 text-xs font-medium text-brand-600">Active opportunity</span>
              </div>
              <p class="text-sm text-gray-700">Shown here because an event has already been created from one of this quote's dates, no viewing booked on it yet.</p>
            </div>

            <div class="rounded-2xl border border-gray-200 bg-white p-5">
              <p class="mb-1 text-xs font-medium tracking-wide text-gray-500 uppercase">Multiple quotes per contact</p>
              <p class="text-sm text-gray-700">Normal. The same couple might submit a quote request several times from the website before booking a viewing, each shows separately here and on the contact record.</p>
            </div>
          </div>
        }
      }
    } @else {
      <!-- ===================== FULL PROCESS (shared) ===================== -->
      <p class="mb-5 text-sm text-gray-500">The full booking journey, start to finish. Same on every page.</p>

      <div class="relative pl-6">
        <div class="absolute top-2 bottom-2 left-[9px] w-0.5 bg-gray-200"></div>

        <div class="relative mb-5">
          <div class="absolute top-0.5 -left-6 h-4 w-4 rounded-full bg-brand-500"></div>
          <p class="text-sm font-semibold text-gray-800">Pre-quote</p>
          <p class="mt-0.5 text-sm text-gray-500">A lead exists, no quote yet.</p>
        </div>

        <div class="relative mb-5">
          <div class="absolute top-0.5 -left-6 h-4 w-4 rounded-full bg-brand-500"></div>
          <p class="text-sm font-semibold text-gray-800">Quote received</p>
          <p class="mt-0.5 text-sm text-gray-500">A quote exists. Can carry many quotes per contact.</p>
        </div>

        <div class="relative mb-5">
          <div class="absolute top-0.5 -left-6 h-4 w-4 rounded-full bg-brand-500"></div>
          <p class="text-sm font-semibold text-gray-800">Active opportunity</p>
          <p class="mt-0.5 text-sm text-gray-500">An event has been created for this prospect, no viewing booked yet.</p>
        </div>

        <div class="relative mb-5">
          <div class="absolute top-0.5 -left-6 h-4 w-4 rounded-full bg-brand-500"></div>
          <p class="text-sm font-semibold text-gray-800">Viewing booked</p>
          <p class="mt-0.5 text-sm text-gray-500">A show-round date is set on the event. <span class="font-medium">This is the ceiling for a quote on its own</span> — a quote can't go further without becoming an event.</p>
        </div>

        <div class="relative mb-5">
          <div class="absolute top-0.5 -left-6 h-4 w-4 rounded-full bg-warning-600"></div>
          <p class="text-sm font-semibold text-gray-800">Provisional</p>
          <p class="mt-0.5 text-sm text-gray-500">Date held, the sale is agreed and the paperwork is happening. Not yet confirmed. Requires an event.</p>
        </div>

        <div class="relative mb-5">
          <div class="absolute top-0.5 -left-6 h-4 w-4 rounded-full bg-success-600"></div>
          <p class="text-sm font-semibold text-gray-800">Confirmed</p>
          <p class="mt-0.5 text-sm text-gray-500">Booking locked in. Only an event can reach this stage.</p>
        </div>

        <div class="relative">
          <div class="absolute top-0.5 -left-6 h-4 w-4 rounded-full bg-gray-400"></div>
          <p class="text-sm font-semibold text-gray-800">Completed</p>
          <p class="mt-0.5 text-sm text-gray-500">Wedding date has passed. Handled by operations from here.</p>
        </div>
      </div>

      <div class="mt-6 rounded-2xl border border-gray-200 bg-gray-25 p-5">
        <p class="mb-1 text-xs font-medium tracking-wide text-gray-500 uppercase">A couple can have</p>
        <p class="text-sm text-gray-700">Multiple quotes, and multiple unconfirmed events running in parallel, for example a cloned event with a small change. Only one ever becomes confirmed. The rest stay visible as history.</p>
      </div>

      <div class="mt-3 rounded-2xl border border-gray-200 bg-gray-25 p-5">
        <p class="mb-1 text-xs font-medium tracking-wide text-gray-500 uppercase">Lost vs cancelled</p>
        <p class="text-sm text-gray-700">Different outcomes. Lost is a prospect who never confirmed. Cancelled is a booking that was confirmed and later fell through.</p>
      </div>
    }
  </app-right-sidebar>
}
